import { ApolloProvider } from "@apollo/client";
import { client } from "./utils/apollo";
import loadable from "@loadable/component";
import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { RequireAuth } from "./components/auth/RequireAuth";
import { StoreCheckout } from "./components/checkout/Checkout.types";

import { useContentfulContext } from "./context/ContentfulContext";
import ShoppingCartContextProvider from "./context/ShoppingCartContext";
import SignedInContextProvider from "./context/SignedInContext";
import { useFeatureFlag } from "./components/common/FeatureFlag";
import {
  CREATE_PASSWORD_URL,
  SIGNUP_ADDRESS_URL,
  SIGNUP_CONTACTINFO_URL,
  SIGNUP_LEGALNAME_URL,
  SIGNUP_SCHEDULE_URL,
  SIGN_UP_ABOUT_YOUR_DATA_URL,
  SIGN_UP_PAYMENT_URL,
  SIGN_UP_REFERRALS_URL,
  SUBSCRIPTION_PLANS_URL,
  isReferralsStepEnabled,
} from "./components/signup/SignUp";
import {
  SIGN_UP_V2_ABOUT_YOUR_DATA_URL,
  SIGN_UP_V2_CREATE_PASSWORD_URL,
  SIGN_UP_V2_INDEX_ROUTE_URL,
  SIGN_UP_V2_LEGALNAME_URL,
  SIGN_UP_V2_PAYMENT_URL,
  SIGN_UP_V2_PHONE_NUMBER_URL,
  SIGN_UP_V2_REFERRALS_URL,
  SIGN_UP_V2_SUBSCRIPTION_PLANS_URL,
  SIGN_UP_V2_VERIFY_PHONE_NUMBER_URL,
} from "./components/signup/SignUpV2";
import UtmsContextProvider from "./context/UTMSContext";
import AuthService from "./components/auth/AuthService";
import { TrackAnalytic } from "./components/common/TrackAnalytic";

const SignUpV2 = loadable(() => import(/* webpackChunkName: "SignUpV2" */ "./components/signup/SignUpV2"), {
  resolveComponent: (components) => components.SignUpV2,
}) as React.ComponentType<{
  setHeaderClass: (v: string | null) => void;
  setWrapperClass: (v: string | null) => void;
  setBypassAuthCheck: (v: boolean) => void;
  setDisplayBackButton: (v: boolean) => void;
}>;

const SignUp = loadable(() => import(/* webpackChunkName: "SignUp" */ "./components/signup/SignUp"), {
  resolveComponent: (components) => components.SignUp,
}) as React.ComponentType<{
  setHeaderClass: (v: string | null) => void;
  setWrapperClass: (v: string | null) => void;
  setBypassAuthCheck: (v: boolean) => void;
  setDisplayBackButton: (v: boolean) => void;
}>;

const PeriodicStatements = loadable(
  () => import(/* webpackChunkName: "PeriodicStatements" */ "./components/periodicStatements/PeriodicStatements"),
  {
    resolveComponent: (components) => components.PeriodicStatements,
  }
) as any;

const Cart = loadable(() => import(/* webpackChunkName: "CartUs" */ "./components/cart/Cart"), {
  resolveComponent: (components) => components.Cart,
}) as any;

const Orders = loadable(() => import(/* webpackChunkName: "Orders" */ "./components/store/orders/Orders"), {
  resolveComponent: (components) => components.Orders,
}) as any;

const CartTimeout = loadable(() => import(/* webpackChunkName: "CartTimeout" */ "./components/cart/CartTimeout"), {
  resolveComponent: (components) => components.CartTimeout,
}) as any;

const ShoppingCartFailure = loadable(
  () => import(/* webpackChunkName: "ShoppingCartFailure" */ "./components/checkout/cart/ShoppingCartFailure")
) as any;

const UnauthorizedRoutesLayout = loadable(
  () => import(/* webpackChunkName: "UnauthorizedRoutesLayout" */ "./components/layout/UnauthorizedRoutesLayout"),
  {
    resolveComponent: (components) => components.UnauthorizedRoutesLayout,
  }
) as any;

const UserCookieConsent = loadable(
  () => import(/* webpackChunkName: "UserCookieConsent" */ "./components/common/UserCookieConsent"),
  {
    resolveComponent: (components) => components.UserCookieConsent,
  }
) as any;

const VideoList = loadable(() => import(/* webpackChunkName: "VideoList" */ "./components/video/VideoList"), {
  resolveComponent: (components) => components.VideoList,
}) as any;

const Layout = loadable(() => import(/* webpackChunkName: "LayoutUs" */ "./components/layout/Layout"), {
  resolveComponent: (components) => components.Layout,
}) as any;

const Logout = loadable(() => import(/* webpackChunkName: "Logout" */ "./components/auth/Logout"), {
  resolveComponent: (components) => components.Logout,
}) as any;

const ResetPassword = loadable(
  () => import(/* webpackChunkName: "ResetPassword" */ "./components/auth/reset-password/ResetPassword")
) as any;

const ChangePassword = loadable(
  () => import(/* webpackChunkName: "ChangePassword" */ "./components/auth/change-password/ResetPassword")
) as any;

const RetailChargeAgreement = loadable(
  () => import(/* webpackChunkName: "RetailChargeAgreement" */ "./components/legal/RetailChargeAgreement"),
  { resolveComponent: (components) => components.RetailChargeAgreement }
) as any;
const ESignConsent = loadable(() => import(/* webpackChunkName: "ESignConsent" */ "./components/legal/ESignConsent"), {
  resolveComponent: (components) => components.ESignConsent,
}) as any;
const UsTermsConditions = loadable(
  () => import(/* webpackChunkName: "UsTermsConditions" */ "./components/legal/UsTermsConditions"),
  { resolveComponent: (components) => components.UsTermsConditions }
) as any;
const UsPrivacyPolicy = loadable(
  () => import(/* webpackChunkName: "UsPrivacyPolicy" */ "./components/legal/UsPrivacyPolicy"),
  { resolveComponent: (components) => components.UsPrivacyPolicy }
) as any;
const Error404 = loadable(() => import(/* webpackChunkName: "Error404Us" */ "./components/error/Error404"), {
  resolveComponent: (components) => components.Error404,
}) as any;

const Settings = loadable(() => import(/* webpackChunkName: "SettingsUs" */ "./components/settings/Settings"), {
  resolveComponent: (components) => components.Settings,
}) as any;

const Store = loadable(() => import(/* webpackChunkName: "StoreUs" */ "./components/store/Store"), {
  resolveComponent: (components) => components.Store,
}) as any;

const Checkout = loadable(() => import(/* webpackChunkName: "CheckoutUs" */ "./components/checkout/Checkout"), {
  resolveComponent: (components) => components.Checkout,
}) as any;

const ProductList = loadable(
  () => import(/* webpackChunkName: "ProductListUs" */ "./components/store/product-list/ProductList"),
  {
    resolveComponent: (components) => components.ProductList,
  }
) as any;

const ProductDetails = loadable(
  () => import(/* webpackChunkName: "ProductDetailsUs" */ "./components/store/product-details/ProductDetails"),
  {
    resolveComponent: (components) => components.ProductDetails,
  }
) as any;

const OurImpact = loadable(() => import(/* webpackChunkName: "OurImpact" */ "./components/ourImpact/OurImpact"), {
  resolveComponent: (components) => components.OurImpact,
}) as any;

const PaymentFailed = loadable(
  () => import(/* webpackChunkName: "Example" */ "./components/store/upcoming-payments/PaymentFailed")
) as any;

const AllStoreCategories = loadable(
  () => import(/* webpackChunkName: "AllStoreCategoriesUs" */ "./components/store/AllStoreCategories"),
  {
    resolveComponent: (components) => components.AllStoreCategories,
  }
) as any;

const Login = loadable(() => import(/* webpackChunkName: "Login" */ "./components/auth/Login"), {
  resolveComponent: (components) => components.Login,
}) as any;

interface IUseApp {
  headerClass: string | null;
  wrapperClass: string | null;
  bypassAuthCheck: boolean;
  displayBackButton: boolean;
  setHeaderClass: (headerClass: string | null) => void;
  setBypassAuthCheck: (bypassAuthCheck: boolean) => void;
  setWrapperClass: (wrapperClass: string | null) => void;
  setDisplayBackButton: (displayBackButton: boolean) => void;
  backButtonRoute: string | undefined;
  checkout: StoreCheckout | undefined;
  signUpRoute: JSX.Element | undefined;
  contentReady: boolean;
  hideCookies: boolean;
}

export const SIGN_UP_FLOW_V2_ACTIVE_KEY = "signupFlowV2";
export const SIGN_UP_DISABLED_KEY = "signupDisabled";

const useApp = (): IUseApp => {
  const [headerClass, setHeaderClass] = useState<string | null>(null);
  const [bypassAuthCheck, setBypassAuthCheck] = useState<boolean>(false);
  const [wrapperClass, setWrapperClass] = useState<string | null>(null);
  const [displayBackButton, setDisplayBackButton] = useState<boolean>(false);
  const [backButtonRoute, setBackButtonRoute] = useState<string | undefined>(undefined);
  const { pathname } = useLocation();
  const [checkout] = useState<StoreCheckout>();
  const [signUpRoute, setSignUpRoute] = useState<JSX.Element | undefined>();
  const [hideCookies, setHideCookies] = useState<boolean>(false);
  const { contentReady } = useContentfulContext();
  const isSignUpFlowV2Active = useFeatureFlag(SIGN_UP_FLOW_V2_ACTIVE_KEY);
  const isSignUpDisabled = useFeatureFlag(SIGN_UP_DISABLED_KEY);
  const navigate = useNavigate();

  const handleWebViewTokenChange = () => {
    const token: string = window?.bitsAccessToken ?? "";
    const route: string = window?.webViewRequestedRoute ?? "";
    const hideCookies = window?.hideCookies === "true" ? true : false;
    setHideCookies(hideCookies);
    if (token) {
      AuthService.saveAuthToken(token);

      if (route) {
        navigate(route);
      } else {
        navigate("/store");
      }
    }
  };

  useEffect(() => {
    window.addEventListener("bitsAccessToken", handleWebViewTokenChange);
    if (window?.bitsAccessToken) {
      handleWebViewTokenChange();
    }
    return () => window.removeEventListener("bitsAccessToken", handleWebViewTokenChange);
  }, []);

  const getBackButtonRouteV2 = (): string | undefined => {
    switch (pathname.toLowerCase()) {
      case SIGN_UP_V2_CREATE_PASSWORD_URL:
        return SIGN_UP_V2_PHONE_NUMBER_URL;
      case SIGN_UP_V2_VERIFY_PHONE_NUMBER_URL:
        return SIGN_UP_V2_VERIFY_PHONE_NUMBER_URL;
      case SIGN_UP_V2_LEGALNAME_URL:
        return SIGN_UP_V2_SUBSCRIPTION_PLANS_URL;
      case SIGN_UP_V2_PHONE_NUMBER_URL:
        return SIGN_UP_V2_INDEX_ROUTE_URL;
      case SIGN_UP_V2_REFERRALS_URL:
        return SIGN_UP_V2_LEGALNAME_URL;
      case SIGN_UP_V2_ABOUT_YOUR_DATA_URL:
        return SIGN_UP_V2_REFERRALS_URL;
      case SIGN_UP_V2_PAYMENT_URL:
        return SIGN_UP_V2_ABOUT_YOUR_DATA_URL;
      default:
        return undefined;
    }
  };

  const getBackButtonRouteV1 = (): string | undefined => {
    switch (pathname.toLowerCase()) {
      case SIGNUP_ADDRESS_URL:
        return SIGNUP_LEGALNAME_URL;
      case SIGNUP_CONTACTINFO_URL:
        return SIGNUP_ADDRESS_URL;
      case CREATE_PASSWORD_URL:
        return SIGNUP_CONTACTINFO_URL;
      case SIGN_UP_ABOUT_YOUR_DATA_URL:
        /* Referrals sits between the password and the data consent in the UK
           flow but is absent from the US one, so where back goes depends on
           the country. This route overrides `SignUp`'s own country-aware
           fallback, so it has to make the same distinction. */
        return isReferralsStepEnabled() ? SIGN_UP_REFERRALS_URL : CREATE_PASSWORD_URL;
      case SUBSCRIPTION_PLANS_URL:
        return SIGN_UP_ABOUT_YOUR_DATA_URL;
      case SIGN_UP_PAYMENT_URL:
        return SUBSCRIPTION_PLANS_URL;
      case SIGNUP_SCHEDULE_URL:
        return SUBSCRIPTION_PLANS_URL;
      default:
        return undefined;
    }
  };

  const getBackButtonRoute = () => (isSignUpFlowV2Active ? getBackButtonRouteV2() : getBackButtonRouteV1());

  const getSignUpRoute = () => {
    if (isSignUpDisabled) {
      return <Navigate to="/login" replace />;
    }
    return isSignUpFlowV2Active ? (
      <SignUpV2
        setHeaderClass={setHeaderClass}
        setWrapperClass={setWrapperClass}
        setBypassAuthCheck={setBypassAuthCheck}
        setDisplayBackButton={setDisplayBackButton}
      />
    ) : (
      <SignUp
        setHeaderClass={setHeaderClass}
        setWrapperClass={setWrapperClass}
        setBypassAuthCheck={setBypassAuthCheck}
        setDisplayBackButton={setDisplayBackButton}
      />
    );
  };

  useEffect(() => {
    setBackButtonRoute(getBackButtonRoute());
  }, [pathname]);

  useEffect(() => {
    if (contentReady) {
      setSignUpRoute(getSignUpRoute());
    }
  }, [isSignUpFlowV2Active, isSignUpDisabled, contentReady]);

  return {
    headerClass,
    bypassAuthCheck,
    setHeaderClass,
    setBypassAuthCheck,
    wrapperClass,
    setWrapperClass,
    displayBackButton,
    setDisplayBackButton,
    backButtonRoute,
    checkout,
    signUpRoute,
    contentReady: contentReady ?? false,
    hideCookies,
  };
};

export const AppWrapper = () => {
  const {
    headerClass,
    wrapperClass,
    bypassAuthCheck,
    displayBackButton,
    backButtonRoute,
    signUpRoute,
    contentReady,
    hideCookies,
  } = useApp();
  if (!contentReady) {
    return null;
  }
  return (
    <>
      <UtmsContextProvider>
        <SignedInContextProvider>
          <ShoppingCartContextProvider>
            <Routes>
              <Route
                element={
                  <RequireAuth>
                    <ApolloProvider client={client}>
                      <Layout />
                    </ApolloProvider>
                  </RequireAuth>
                }
              >
                <Route path="/settings/*" element={<Settings />} />
                <Route path="/store">
                  <Route path="categories" element={<AllStoreCategories />} />
                  <Route path="category/:slug" element={<ProductList />} />
                  <Route path="product/:id/:slug" element={<ProductDetails />} />
                  <Route path="orders/*" element={<Orders />} />
                  <Route index element={<Store />} />
                </Route>
                <Route path="/checkout/*" element={<Checkout />} />
                <Route path="/ourimpact/*" element={<OurImpact />} />
                <Route
                  path="/video"
                  element={<VideoList playlistId={process.env.REACT_APP_YOUTUBE_PLAYLIST_ID ?? ""} />}
                />
                <Route path="/paymentError" element={<PaymentFailed />} />
                <Route path="/statements" element={<PeriodicStatements />} />
                <Route path="/store/cart" element={<Cart />} />
                <Route path="/store/cart/error" element={<ShoppingCartFailure />} />
                {/* useCheckout navigates to a relative "error" on failure. From the
                    product page that resolves here; without this route it fell
                    through to the 404 and the failure was invisible to the user. */}
                <Route path="/store/product/:id/:slug/error" element={<ShoppingCartFailure />} />
                <Route path="/store/timeout" element={<CartTimeout />} />
              </Route>
              <Route
                path="/"
                element={
                  <UnauthorizedRoutesLayout
                    headerClass={headerClass}
                    wrapperClass={wrapperClass}
                    bypassAuthCheck={bypassAuthCheck}
                    displayBackButton={displayBackButton}
                    backButtonRoute={backButtonRoute}
                  />
                }
              >
                <Route path="login" element={<Login />} />
                <Route path="forgotPassword" element={<ResetPassword />} />
                <Route path="signup/*" element={signUpRoute} />
                <Route path="changepassword" element={<ChangePassword />} />
                <Route index element={signUpRoute} />
              </Route>
              <Route path="logout" element={<Logout />} />
              {/* Public, and outside both layout groups on purpose. The US
                  footer links to these from every signed-in page, the signup
                  consent step links to them before an account exists, and the
                  post-signup modal links to them from inside the store — a
                  document you must accept to open a credit line has to be
                  readable in all three states. `ApolloProvider` because the
                  shell they render includes `Navigation`, whose mobile search
                  box queries Saleor. */}
              <Route
                path="/legal/retail-charge-agreement"
                element={
                  <ApolloProvider client={client}>
                    <RetailChargeAgreement />
                  </ApolloProvider>
                }
              />
              <Route
                path="/legal/e-sign"
                element={
                  <ApolloProvider client={client}>
                    <ESignConsent />
                  </ApolloProvider>
                }
              />
              {/* The US terms and privacy policy, for the same reason and from
                  the same place. These two had a further problem: the links
                  were repointed at `www.getbits.app/terms-conditions` and
                  `/privacy-policy` when the `/us` pages started 404ing, on the
                  assumption that the same slugs served the US documents. They
                  do not — they serve Fea Card Limited's, the UK entity — while
                  the consent line tells a US applicant they are agreeing to
                  Bits Card, Inc's. */}
              <Route
                path="/legal/terms-conditions"
                element={
                  <ApolloProvider client={client}>
                    <UsTermsConditions />
                  </ApolloProvider>
                }
              />
              <Route
                path="/legal/privacy-policy"
                element={
                  <ApolloProvider client={client}>
                    <UsPrivacyPolicy />
                  </ApolloProvider>
                }
              />
              <Route
                path="*"
                element={
                  <ApolloProvider client={client}>
                    <Error404 />
                  </ApolloProvider>
                }
              />
            </Routes>
          </ShoppingCartContextProvider>
          <TrackAnalytic />
        </SignedInContextProvider>
      </UtmsContextProvider>
      {!hideCookies && <UserCookieConsent />}
    </>
  );
};
